/**
 * API field catalog for report / proposal templates.
 *
 * - The Configuration page lets admins pick fields. Picked fields travel to the
 *   AI agent and live in the saved template HTML as
 *     <span class="f" data-ph="Label" data-field="project.name"></span>
 * - The project Reports view reads those `data-field` keys, loads the selected
 *   project's API data and fills each span. Fields the API does not return stay
 *   empty.
 *
 * Two kinds of keys:
 *   1. Curated keys   - friendly names such as `project.name`, `contacts.nettles.project_manager.email`.
 *   2. Raw API keys   - `api.<source>.<path>`, discovered from a live project response
 *                       (for example `api.opportunity.Project__r.Name`).
 */

export const FIELD_ATTR = "data-field";
export const RAW_PREFIX = "api.";

/* ------------------------------------------------------------------ */
/* Sources (one entry per backend call needed to fill a project)       */
/* ------------------------------------------------------------------ */

export const SOURCES = {
  opportunity: {
    label: "Project (Salesforce opportunity)",
    endpoint: "/api/salesforce/opportunity/:id",
  },
  info: {
    label: "Project Info (Salesforce)",
    endpoint: "/api/salesforce/projects/:projectId/Info",
  },
  contacts: {
    label: "Contacts",
    endpoint: "/api/contacts/opportunity/:opportunityNo",
  },
  financial: {
    label: "Financial Performance",
    endpoint: "/api/finicial-budget",
  },
  schedule: {
    label: "Scheduling metrics",
    endpoint: "/api/schedules/:opportunityNo",
  },
  outlook: {
    label: "Next on-site (Outlook calendar)",
    endpoint: "/api/opportunity-events/:opportunityNo",
  },
  onsite: {
    label: "On-site dates (Miter)",
    endpoint: "/api/miter/production-performance/:projectNo/onsite-dates",
  },
  production: {
    label: "Production Performance (Miter)",
    endpoint: "/api/miter/production-performance/:projectNo",
  },
};

export const SOURCE_IDS = Object.keys(SOURCES);

/* ------------------------------------------------------------------ */
/* Small value helpers                                                 */
/* ------------------------------------------------------------------ */

export function getPath(obj, path) {
  if (obj == null || !path) return undefined;
  const parts = String(path).replace(/\[(\d+)\]/g, ".$1").split(".").filter(Boolean);
  let cur = obj;
  for (const part of parts) {
    if (cur == null) return undefined;
    cur = cur[part];
  }
  return cur;
}

/** Plain text for a value; "-" and objects count as empty. */
export function textOf(value) {
  if (value == null) return "";
  if (typeof value === "object") return "";
  const s = String(value).trim();
  return s === "-" ? "" : s;
}

function pick(...values) {
  for (const v of values) {
    const s = textOf(v);
    if (s) return s;
  }
  return "";
}

function toNumber(v) {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/[$,%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function money(v) {
  const n = toNumber(v);
  if (n == null) return "";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function percent(v) {
  const n = toNumber(v);
  if (n == null) return "";
  return `${Math.round(n * 100) / 100}%`;
}

export function usDate(value) {
  const s = textOf(value);
  if (!s) return "";
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

function joinAddress(street, city, state, zip) {
  return [street, city, state, zip].map(textOf).filter(Boolean).join(", ");
}

const slug = (s) =>
  String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/* ------------------------------------------------------------------ */
/* Context accessors                                                   */
/* ------------------------------------------------------------------ */

const opp = (c) => c?.raw?.opportunity || {};
const proj = (c) => opp(c).Project__r || {};
const cust = (c) => opp(c).Contract_With__r || {};
const sel = (c) => c?.selected || {};
const info = (c) => c?.raw?.info || {};

function contactList(c) {
  const raw = c?.raw?.contacts;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

function scheduleData(c) {
  const raw = c?.raw?.schedule;
  return raw?.data || raw || {};
}

function budgetValue(c, label) {
  const list = c?.raw?.financial?.budgetProgressData;
  if (!Array.isArray(list)) return undefined;
  return list.find((item) => item?.label === label)?.value;
}

function usedOf(used, total) {
  const u = textOf(used);
  const t = textOf(total);
  if (!u && !t) return "";
  return `${u || "0"}/${t || "0"}`;
}

/* ------------------------------------------------------------------ */
/* Curated field definitions                                           */
/* ------------------------------------------------------------------ */

export const FIELD_GROUPS = [
  { id: "project", label: "Project Details", hint: "Fields related to the project and its opportunity" },
  { id: "customer", label: "Customer", hint: "Contract-with company details" },
  { id: "info", label: "Project Info", hint: "Billing, tax, payroll and bond terms" },
  { id: "contacts", label: "Contacts", hint: "Project team, GC / customer, designers and vendors" },
  { id: "financial", label: "Financial Performance", hint: "Billing progress, revenue and expense budgets" },
  { id: "schedule", label: "Scheduling", hint: "Mobs, days, trips and on-site dates" },
  { id: "report", label: "Report", hint: "Values generated when the report opens" },
];

const FIELDS = [];

function add(group, key, label, sources, get, sub) {
  FIELDS.push({ key, label, group, sub: sub || null, sources, get });
}

/* Project details */
const P = "project";
add(P, "project.name", "Project Name", ["opportunity"], (c) => pick(proj(c).Name, sel(c).projectName));
add(P, "project.number", "Project No.", ["opportunity"], (c) =>
  pick(proj(c).Project_System_Wide_ID__c, sel(c).projectId, sel(c).projectNo)
);
add(P, "project.opportunity_name", "Opportunity Name", ["opportunity"], (c) => pick(opp(c).Name, sel(c).opportunityName));
add(P, "project.opportunity_no", "Opportunity No.", ["opportunity"], (c) =>
  pick(opp(c).Opportunity_System_Wide_ID__c, sel(c).opportunityNo, sel(c).opportunityUUID)
);
add(P, "project.status", "Project Status", ["opportunity"], (c) => pick(opp(c).Status, sel(c).projectStatus));
add(P, "project.division", "Division", ["opportunity"], (c) => pick(opp(c).dv_Division_New__c, sel(c).division));
add(P, "project.company", "Nettles Company / Branch", ["opportunity"], (c) => pick(opp(c).Company__c, sel(c).company));
add(P, "project.scope", "Scope Type", ["opportunity"], (c) => pick(opp(c).dv_Type__c, sel(c).scope));
add(P, "project.delivery_method", "Delivery Method", ["opportunity"], (c) =>
  pick(opp(c).Delivery_Method__c, sel(c).deliveryMethod)
);
add(P, "project.scope_description", "Scope Description", ["opportunity"], (c) =>
  pick(opp(c).Project_Scope__c, sel(c).scopeDescription)
);
add(P, "project.street", "Project Street", ["opportunity"], (c) => pick(proj(c).Street__c, sel(c).street));
add(P, "project.city", "Project City", ["opportunity"], (c) => pick(proj(c).gmb_City__c, sel(c).city));
add(P, "project.state", "Project State", ["opportunity"], (c) => pick(proj(c).gmb_Project_State__c, sel(c).state));
add(P, "project.zip", "Project Zip Code", ["opportunity"], (c) => pick(proj(c).Zip_Code__c, sel(c).zipCode));
add(P, "project.address", "Project Address (full)", ["opportunity"], (c) => {
  const full = joinAddress(
    pick(proj(c).Street__c, sel(c).street),
    pick(proj(c).gmb_City__c, sel(c).city),
    pick(proj(c).gmb_Project_State__c, sel(c).state),
    pick(proj(c).Zip_Code__c, sel(c).zipCode)
  );
  return full || pick(sel(c).projectAddress);
});
add(P, "project.estimating_folder", "Estimating Folder Link", ["opportunity"], (c) =>
  pick(proj(c).Nettles_Estimating_Folder_Link__c, sel(c).estimatingFolderLink)
);

/* Customer */
const C = "customer";
add(C, "customer.name", "Customer / GC Name", ["opportunity"], (c) => pick(cust(c).Name, sel(c).customerName1, sel(c).customerName));
add(C, "customer.street", "Customer Billing Street", ["opportunity"], (c) => pick(cust(c).BillingStreet, sel(c).customerStreet));
add(C, "customer.city", "Customer Billing City", ["opportunity"], (c) => pick(cust(c).BillingCity, sel(c).customerCity));
add(C, "customer.state", "Customer Billing State", ["opportunity"], (c) => pick(cust(c).BillingState, sel(c).customerState));
add(C, "customer.zip", "Customer Billing Zip", ["opportunity"], (c) =>
  pick(cust(c).BillingPostalCode, sel(c).customerZipCode)
);
add(C, "customer.address", "Customer Billing Address (full)", ["opportunity"], (c) =>
  joinAddress(
    pick(cust(c).BillingStreet, sel(c).customerStreet),
    pick(cust(c).BillingCity, sel(c).customerCity),
    pick(cust(c).BillingState, sel(c).customerState),
    pick(cust(c).BillingPostalCode, sel(c).customerZipCode)
  )
);

/* Project info */
const I = "info";
add(I, "info.bill_date", "Billing Date", ["info"], (c) => pick(info(c).bill_date, sel(c).billingDate, sel(c).bill_date));
add(I, "info.contract", "Contract", ["info"], (c) => pick(info(c).contract, sel(c).contract));
add(I, "info.tax_exempt", "Tax Exempt", ["info"], (c) => pick(info(c).tax_exempt, sel(c).taxExempt));
add(I, "info.certified_payroll", "Certified Payroll", ["info"], (c) =>
  pick(info(c).certified_payroll, sel(c).certifiedPayroll)
);
add(I, "info.p_and_p_bonds", "P&P Bonds", ["info"], (c) => pick(info(c).p_and_p_bonds, sel(c).ppBonds));
add(I, "info.ccip", "CCIP", ["info"], (c) => pick(info(c).ccip, sel(c).ccip));
add(I, "info.retainage", "Retainage", ["info"], (c) => pick(info(c).retainage, sel(c).retainage));
add(I, "info.prevailing_wage", "Prevailing Wage", ["info"], (c) =>
  pick(info(c).prevailing_wage, sel(c).prevailingWage)
);

/* Contacts: Group / ContactType come straight from the contacts API */
export const CONTACT_GROUPS = [
  {
    id: "nettles",
    group: "Nettles Project Team",
    roles: ["Operations Manager", "Project Manager", "Project Accountant", "Submittals", "Salesperson", "Superintendent"],
  },
  {
    id: "gc",
    group: "GC/Customer",
    roles: ["Project Manager", "Asst. Project Manager", "Superintendent", "Project Engineer", "Billing Contact"],
  },
  {
    id: "design",
    group: "Designer/Consultant",
    roles: ["Architect", "Engineer", "Inspector", "Consultant"],
  },
];

const CONTACT_PROPS = [
  { id: "name", label: "Name", get: (r) => pick(`${textOf(r.FirstName)} ${textOf(r.LastName)}`) },
  { id: "email", label: "Email", get: (r) => pick(r.Email) },
  { id: "phone", label: "Phone", get: (r) => pick(r.Phone) },
  { id: "mobile", label: "Mobile", get: (r) => pick(r.MobilePhone) },
  { id: "company", label: "Company", get: (r) => pick(r.AccountName) },
  { id: "title", label: "Job Title", get: (r) => pick(r.Title) },
];

function addContactFields(prefix, sub, finder) {
  CONTACT_PROPS.forEach((prop) => {
    add(
      "contacts",
      `contacts.${prefix}.${prop.id}`,
      `${sub} - ${prop.label}`,
      ["contacts"],
      (c) => {
        const row = finder(contactList(c));
        return row ? prop.get(row) : "";
      },
      sub
    );
  });
}

CONTACT_GROUPS.forEach(({ id, group, roles }) => {
  roles.forEach((role) => {
    addContactFields(
      `${id}.${slug(role)}`,
      `${group} / ${role}`,
      (list) => list.find((r) => r?.Group === group && r?.ContactType === role)
    );
  });
});
[1, 2, 3].forEach((n) => {
  addContactFields(`vendor_${n}`, `Vendors / Vendor ${n}`, (list) => list.filter((r) => r?.Group === "Vendors")[n - 1]);
});

/* Financial performance */
const F = "financial";
const finField = (key, label, apiLabel, fmt) =>
  add(F, key, label, ["financial"], (c) => fmt(budgetValue(c, apiLabel)));
finField("financial.billing_progress", "Billing Progress", "Billing Progress", percent);
finField("financial.cost_performance", "Cost Performance", "Cost Performance", percent);
finField("financial.actual_revenue", "Actual Revenue Amount", "Actual Revenue Amount", money);
finField("financial.revised_revenue", "Revised Revenue Budget", "Revised Revenue Budget", money);
finField("financial.actual_expense", "Actual Expense Amount", "Actual Expense Amount", money);
finField("financial.revised_expense", "Revised Expense Budget", "Revised Budget Expense", money);

/* Scheduling */
const S = "schedule";
add(S, "schedule.mobs", "Mobs Used / Contracted", ["schedule"], (c) =>
  usedOf(scheduleData(c).mobs_used, scheduleData(c).mobs_contracted)
);
add(S, "schedule.mobs_used", "Mobs Used", ["schedule"], (c) => pick(scheduleData(c).mobs_used));
add(S, "schedule.mobs_contracted", "Mobs Contracted", ["schedule"], (c) => pick(scheduleData(c).mobs_contracted));
add(S, "schedule.days", "Days Used / Estimated", ["schedule"], (c) =>
  usedOf(scheduleData(c).days_used, scheduleData(c).days_estimated)
);
add(S, "schedule.days_used", "Days Used", ["schedule"], (c) => pick(scheduleData(c).days_used));
add(S, "schedule.days_estimated", "Days Estimated", ["schedule"], (c) => pick(scheduleData(c).days_estimated));
add(S, "schedule.trips", "Trips Used / Estimated", ["schedule"], (c) =>
  usedOf(scheduleData(c).trip_used, scheduleData(c).trip_estimated)
);
add(S, "schedule.trips_used", "Trips Used", ["schedule"], (c) => pick(scheduleData(c).trip_used));
add(S, "schedule.trips_estimated", "Trips Estimated", ["schedule"], (c) => pick(scheduleData(c).trip_estimated));
add(S, "schedule.first_onsite", "First Onsite", ["onsite"], (c) =>
  usDate(getPath(c?.raw?.onsite, "data.first_onsite"))
);
add(S, "schedule.most_recent_onsite", "Most Recent Onsite", ["onsite"], (c) =>
  usDate(getPath(c?.raw?.onsite, "data.most_recent_onsite"))
);
add(S, "schedule.next_onsite", "Next Onsite", ["outlook"], (c) => usDate(getPath(c?.raw?.outlook, "next_onsite")));

/* Report (no API call) */
const R = "report";
add(R, "report.date", "Report Date", [], (c) => localDate(c?.meta?.today));
add(R, "report.prepared_by", "Prepared By", [], (c) => pick(c?.meta?.preparedBy));

function localDate(d) {
  const date = d instanceof Date ? d : new Date();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${date.getFullYear()}`;
}

export const CURATED_FIELDS = FIELDS;
const BY_KEY = new Map(FIELDS.map((f) => [f.key, f]));

/* ------------------------------------------------------------------ */
/* Raw API fields (discovered from a live project response)            */
/* ------------------------------------------------------------------ */

const MAX_RAW_PER_SOURCE = 500;
const MAX_DEPTH = 6;
const SKIP_KEYS = new Set(["attributes"]);

/** Flatten a response into leaf paths. Arrays contribute their first item as `[0]`. */
export function discoverRawFields(sourceId, payload) {
  const out = [];
  const walk = (value, path, depth) => {
    if (out.length >= MAX_RAW_PER_SOURCE || depth > MAX_DEPTH) return;
    if (value == null || typeof value !== "object") {
      if (path) out.push({ path, sample: textOf(value) });
      return;
    }
    if (Array.isArray(value)) {
      if (value.length) walk(value[0], `${path}[0]`, depth + 1);
      return;
    }
    Object.keys(value).forEach((k) => {
      if (SKIP_KEYS.has(k)) return;
      walk(value[k], path ? `${path}.${k}` : k, depth + 1);
    });
  };
  walk(payload, "", 0);
  return out.map(({ path, sample }) => ({
    key: `${RAW_PREFIX}${sourceId}.${path}`,
    label: path,
    group: "api",
    sub: SOURCES[sourceId]?.label || sourceId,
    sourceId,
    sources: [sourceId],
    sample,
  }));
}

export function isRawKey(key) {
  return typeof key === "string" && key.startsWith(RAW_PREFIX);
}

function parseRawKey(key) {
  const rest = key.slice(RAW_PREFIX.length);
  const dot = rest.indexOf(".");
  if (dot < 0) return null;
  const sourceId = rest.slice(0, dot);
  if (!SOURCES[sourceId]) return null;
  return { sourceId, path: rest.slice(dot + 1) };
}

/* ------------------------------------------------------------------ */
/* Resolve / label / source lookups                                    */
/* ------------------------------------------------------------------ */

export function isKnownKey(key) {
  return isRawKey(key) ? Boolean(parseRawKey(key)) : BY_KEY.has(key);
}

/** Value for a field key against a loaded project context ("" when the API has no value). */
export function resolveFieldValue(key, ctx) {
  if (!key) return "";
  if (isRawKey(key)) {
    const parsed = parseRawKey(key);
    if (!parsed) return "";
    return textOf(getPath(ctx?.raw?.[parsed.sourceId], parsed.path));
  }
  const def = BY_KEY.get(key);
  if (!def) return "";
  try {
    return textOf(def.get(ctx));
  } catch {
    return "";
  }
}

export function sourcesForKeys(keys) {
  const needed = new Set();
  keys.forEach((key) => {
    if (isRawKey(key)) {
      const parsed = parseRawKey(key);
      if (parsed) needed.add(parsed.sourceId);
      return;
    }
    BY_KEY.get(key)?.sources.forEach((s) => needed.add(s));
  });
  return needed;
}

export function describeField(key) {
  if (isRawKey(key)) {
    const parsed = parseRawKey(key);
    return {
      key,
      label: parsed ? parsed.path : key,
      group: "api",
      groupLabel: "All API fields",
      sub: parsed ? SOURCES[parsed.sourceId].label : "",
    };
  }
  const def = BY_KEY.get(key);
  if (!def) return { key, label: key, group: "unknown", groupLabel: "Other", sub: "" };
  const group = FIELD_GROUPS.find((g) => g.id === def.group);
  return { key, label: def.label, group: def.group, groupLabel: group?.label || def.group, sub: def.sub || "" };
}

/* ------------------------------------------------------------------ */
/* Template HTML helpers                                               */
/* ------------------------------------------------------------------ */

const escAttr = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
const escText = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function fieldSpan(key, label) {
  return `<span class="f" contenteditable="true" data-ph="${escAttr(label)}" ${FIELD_ATTR}="${escAttr(key)}"></span>`;
}

function parseHtml(html) {
  return new DOMParser().parseFromString(html, "text/html");
}

function serialize(doc) {
  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
}

/** Field keys already present in a template. */
export function extractFieldKeys(html) {
  if (!html || typeof html !== "string") return [];
  const keys = [];
  const re = new RegExp(`${FIELD_ATTR}\\s*=\\s*"([^"]+)"`, "gi");
  let m;
  while ((m = re.exec(html))) {
    const key = m[1].replace(/&quot;/g, '"').replace(/&amp;/g, "&");
    if (!keys.includes(key)) keys.push(key);
  }
  return keys;
}

/**
 * Guarantee that every selected field exists in the template. Missing ones are added
 * to an "Project data" section at the end of the page, so the AI can never silently drop a field.
 * `fields` = [{ key, label, group, sub? }]
 */
export function ensureFieldsInHtml(html, fields) {
  if (!html || !fields?.length) return { html, added: 0 };
  const present = new Set(extractFieldKeys(html));
  const missing = fields.filter((f) => !present.has(f.key));
  if (!missing.length) return { html, added: 0 };

  const doc = parseHtml(html);
  const host =
    doc.querySelector(".page") ||
    doc.querySelector("article, main") ||
    doc.body;

  const bucket = new Map();
  missing.forEach((f) => {
    const title = f.groupLabel || describeField(f.key).groupLabel || "Project data";
    if (!bucket.has(title)) bucket.set(title, []);
    bucket.get(title).push(f);
  });

  const section = doc.createElement("section");
  section.setAttribute("data-field-section", "true");
  section.setAttribute("style", "margin-top:18px");
  let inner = `<h3 style="margin:0 0 8px;font-size:13px;letter-spacing:.04em;text-transform:uppercase;border-bottom:1px solid #d6d3d1;padding-bottom:4px">Project data</h3>`;
  bucket.forEach((list, title) => {
    inner += `<table style="width:100%;border-collapse:collapse;margin-bottom:10px"><tbody>`;
    inner += `<tr><td colspan="2" style="padding:4px 0;font-size:11px;font-weight:700;color:#78716c;text-transform:uppercase">${escText(title)}</td></tr>`;
    list.forEach((f) => {
      inner += `<tr><td style="width:40%;padding:3px 8px 3px 0;vertical-align:top;font-weight:600">${escText(f.label)}</td><td style="padding:3px 0">${fieldSpan(f.key, f.label)}</td></tr>`;
    });
    inner += `</tbody></table>`;
  });
  section.innerHTML = inner;
  host.appendChild(section);

  return { html: serialize(doc), added: missing.length };
}

/** Prompt block that tells the AI which API fields to place in the template. */
export function buildFieldsPrompt(fields, samples = {}) {
  if (!fields?.length) return "";
  const lines = fields.map((f) => {
    const sample = samples[f.key] ? `  (example: ${String(samples[f.key]).slice(0, 60)})` : "";
    const where = [f.groupLabel, f.sub].filter(Boolean).join(" / ");
    return `- key="${f.key}"  label="${f.label}"  section="${where}"${sample}`;
  });
  return [
    "PROJECT DATA FIELDS (selected by the admin from the Nettles API):",
    ...lines,
    "",
    "Rules for these fields:",
    `1. Place EVERY listed field in the template, in a sensible labelled spot (group related fields: project details together, contacts together, and so on).`,
    `2. Render each one exactly as: <span class="f" contenteditable="true" data-ph="LABEL" ${FIELD_ATTR}="KEY"></span> - copy KEY exactly as listed and leave the span empty.`,
    "3. The app fills the values for each project later from the API. Do not invent or type values for these fields.",
    `4. When editing an existing template, keep every ${FIELD_ATTR} span that is already there unless the user asks you to remove it.`,
  ].join("\n");
}

/* ------------------------------------------------------------------ */
/* Fill a saved template with one project's values                     */
/* ------------------------------------------------------------------ */

const TEXT_TOKEN = /\{\{\s*([A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z0-9_[\]]+)+)\s*\}\}/g;

/**
 * Fill `[data-field]` spans (and dotted `{{key}}` text tokens) with API values.
 * Fields without a value stay empty so the template's own "empty" styling shows them.
 * Returns { html, filled, missing: [{key,label}], total }.
 */
export function fillTemplateHtml(html, ctx) {
  const doc = parseHtml(html);
  let filled = 0;
  const missing = [];

  doc.querySelectorAll(`[${FIELD_ATTR}]`).forEach((el) => {
    const key = el.getAttribute(FIELD_ATTR);
    const value = resolveFieldValue(key, ctx);
    el.textContent = value;
    el.removeAttribute("data-empty");
    if (value) {
      filled += 1;
    } else {
      missing.push({ key, label: el.getAttribute("data-ph") || describeField(key).label });
    }
  });

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  textNodes.forEach((node) => {
    if (!node.nodeValue || !node.nodeValue.includes("{{")) return;
    const parent = node.parentNode?.nodeName;
    if (parent === "SCRIPT" || parent === "STYLE") return;
    node.nodeValue = node.nodeValue.replace(TEXT_TOKEN, (all, key) => {
      if (!isKnownKey(key)) return all;
      const value = resolveFieldValue(key, ctx);
      if (value) filled += 1;
      else missing.push({ key, label: key });
      return value;
    });
  });

  return {
    html: serialize(doc),
    filled,
    missing,
    total: filled + missing.length,
  };
}

/** Keys the project report must load data for (data-field spans plus dotted tokens). */
export function templateFieldKeys(html) {
  const keys = new Set(extractFieldKeys(html));
  if (html) {
    let m;
    const re = new RegExp(TEXT_TOKEN.source, "g");
    while ((m = re.exec(html))) {
      if (isKnownKey(m[1])) keys.add(m[1]);
    }
  }
  return [...keys];
}
